import { Suspense, useCallback, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { FormattedMessage, useIntl } from 'react-intl'
import { usePageConfig } from '@hooks/usePageConfig'
import { useTableHighlight } from '@hooks/useTableHighlight'
import type { ResourceScope } from 'src/types/global'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import ErrorMessage from '@components/ErrorBoundary/ErrorMessage'
import Loader from '@components/Loader/Loader'
import Summary from '@components/Summary/Summary'
import DashboardList from './DashboardList/DashboardList'
import ExternalHeaderContent from '@components/ExternalHeaderContent/ExternalHeaderContent'
import HeroBanner from '@components/HeroBanner/HeroBanner'
import SortSelector from '@components/SortSelector/SortSelector'
import FilterSelector from '@components/FilterSelector/FilterSelector'
import ItemListSkeleton from '@components/skeletons/ItemListSkeleton/ItemListSkeleton'
import Tabs from '@components/Tabs/Tabs'
import { useDashboardListData } from '@hooks/useDashboardListData'

import './Dashboards.css'

const DashboardsSummary = () => {
  const yours = useDashboardListData('own')
  const total = useDashboardListData('public')
  return (
    <Summary
      items={[
        { label: 'yours', value: yours.length },
        { label: 'total', value: total.length }
      ]}
      id="dashboards"
    />
  )
}

export default function Dashboards() {
  const intl = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const gridRef = useRef<HTMLDivElement>(null)

  const currentScope: ResourceScope =
    searchParams.get('scope') === 'public' ? 'public' : 'own'

  useTableHighlight(gridRef, searchQuery)

  const headerContent = useMemo(() => {
    const crumbs = [
      {
        label: intl.formatMessage({ id: 'nav.dashboards' }),
        path: '/dashboards'
      }
    ]
    return (
      <ExternalHeaderContent
        hasBack
        crumbs={crumbs}
        headerActions={
          <Link className="highlight-button button" to="/dashboard-builder/new">
            <span className="icon icon-dashboard-stroke" />
            <span>
              <FormattedMessage id="new-dashboard" />
            </span>
          </Link>
        }
      />
    )
  }, [intl])

  usePageConfig('dashboards', headerContent)

  const tabs = useMemo(
    () => [
      { id: 'own', label: intl.formatMessage({ id: 'tab.own-dashboards' }) },
      { id: 'public', label: intl.formatMessage({ id: 'tab.public' }) }
    ],
    [intl]
  )

  const handleTabChange = useCallback(
    (id: string) => {
      setSearchParams((prev) => {
        const sort = prev.get('sort')
        const next = new URLSearchParams()
        if (id === 'public') next.set('scope', 'public')
        if (sort) next.set('sort', sort)
        return next
      })
    },
    [setSearchParams]
  )

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
    },
    []
  )
  return (
    <section id="dashboards-content" className="main-content">
      <HeroBanner nameId="dashboards">
        <ErrorBoundary errorComponent={ErrorMessage}>
          <Suspense fallback={<Loader />}>
            <DashboardsSummary />
          </Suspense>
        </ErrorBoundary>
      </HeroBanner>
      <div id="dashboards-display">
        <div className="container">
          <Tabs
            tabs={tabs}
            activeTab={currentScope}
            onChange={handleTabChange}
          />
        </div>
        <header>
          <div className="container">
            <search className="search-highlight">
              <input
                name="search-dashboard"
                type="search"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={intl.formatMessage({
                  id: 'search-data.placeholder'
                })}
              />
            </search>
            <div className="selectors-wrapper">
              {currentScope !== 'public' && <FilterSelector />}
              <SortSelector />
            </div>
          </div>
        </header>
        <div id="dashboards-grid" ref={gridRef}>
          <div className="container">
            <ErrorBoundary errorComponent={ErrorMessage}>
              <Suspense fallback={<ItemListSkeleton />}>
                <DashboardList scope={currentScope} />
              </Suspense>
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </section>
  )
}
